import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CandidateAnalytics } from '../../types';
import apiService from '../../services/api';
import StorageUsage from '../../components/StorageUsage';
import SupportTicketForm from '../../components/SupportTicketForm';
import { useCreditBalanceViewModel } from './CompanySettings/CreditHistory/viewmodel/getBalanceViewModle';
import { useBalance } from '../../contexts/BalanceContext';

const CompanyDashboard: React.FC = () => {
  const { user, loading, canManageUsers, canUploadResumes, canManageJobs } = useAuth();
  const [analytics, setAnalytics] = useState<CandidateAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const { fetchBalance,handleTrialStatus,trailStatus } = useBalance();
  const isSubscriptionAllowed = trailStatus === null ? true : !!(trailStatus?.has_subscription || trailStatus?.is_trial);
  // Fetch analytics when user and loading are ready
  useEffect(() => {
    if (!loading && user) {
      const companyId = user.company_id || user.company?.id;
      if (!companyId && !user.is_super_admin) {
        setError('Your account is not linked to a company. Please contact support.');
        setAnalyticsLoading(false);
        return;
      }
      loadAnalytics();
    }
    // eslint-disable-next-line
  }, [loading, user]);

  // Fetch balance only once on mount
  useEffect(() => {
    fetchBalance();
    handleTrialStatus();
    // eslint-disable-next-line
  }, []);

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const data = await apiService.getCandidateAnalytics();
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  if (loading || analyticsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Welcome back, {user?.first_name}! Here's your recruitment overview.
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-4">Total Candidates</p>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-semibold text-gray-900">
                  {analytics?.total_candidates || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-4">New This Month</p>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-semibold text-gray-900">
                  {analytics?.new_candidates_this_month || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-4">Avg AI Match Score</p>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-semibold text-gray-900">
                  {analytics?.ai_processing_stats?.avg_confidence_score 
                    ? `${(analytics.ai_processing_stats.avg_confidence_score * 100).toFixed(1)}%`
                    : '0%'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-4">Success Rate</p>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-semibold text-gray-900">
                  {analytics?.ai_processing_stats?.success_rate 
                    ? `${Math.round(analytics.ai_processing_stats.success_rate)}%`
                    : '0%'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Storage Usage Card */}
        <StorageUsage showDetails={false} className="col-span-1" />
      </div>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Top Skills */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Skills</h3>
          {analytics?.top_skills && analytics.top_skills.length > 0 ? (
            <div className="space-y-4">
              {analytics.top_skills.slice(0, 8).map((skill, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900">
                      {skill.skill_name}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">
                      ({skill.avg_proficiency.toFixed(1)}/5.0 avg)
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full" 
                        style={{ 
                          width: `${Math.min(100, (skill.candidate_count / analytics.total_candidates) * 100)}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-8 text-right">
                      {skill.candidate_count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No skills data available</p>
          )}
        </div>

        {/* Experience Distribution */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Experience Distribution</h3>
          {analytics?.experience_distribution && analytics.experience_distribution.length > 0 ? (
            <div className="space-y-4">
              {analytics.experience_distribution.map((exp, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    {exp.experience_range}
                  </span>
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${exp.percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-12 text-right">
                      {exp.percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No experience data available</p>
          )}
        </div>
      </div>

      {/* Location Distribution */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Locations</h3>
        {analytics?.location_distribution && analytics.location_distribution.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.location_distribution.slice(0, 9).map((location, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">{location.location}</p>
                  <p className="text-xs text-gray-500">{location.percentage.toFixed(1)}% of candidates</p>
                </div>
                <span className="text-lg font-semibold text-indigo-600">
                  {location.candidate_count}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No location data available</p>
        )}
      </div>

      {/* Industry Sectors */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Industry Sectors</h3>
        {analytics?.sector_distribution && analytics.sector_distribution.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.sector_distribution.slice(0, 9).map((sector, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-purple-900 mb-1">{sector.sector_name}</p>
                  <p className="text-xs text-purple-600">{sector.percentage}% of candidates</p>
                  <div className="w-full bg-purple-200 rounded-full h-1.5 mt-2">
                    <div 
                      className="bg-purple-600 h-1.5 rounded-full" 
                      style={{ width: `${Math.min(100, sector.percentage)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="ml-4 text-center">
                  <span className="text-lg font-bold text-purple-700">
                    {sector.candidate_count}
                  </span>
                  <p className="text-xs text-purple-500">candidates</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p className="text-gray-500">No sector data available</p>
            <p className="text-sm text-gray-400 mt-1">Upload resumes to see industry sector distribution</p>
          </div>
        )}
      </div>

      {/* Comprehensive Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Top Certifications */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Certifications</h3>
          {analytics?.top_certifications && analytics.top_certifications.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_certifications.map((cert, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-900">{cert.certification_name}</p>
                    <p className="text-xs text-green-600">{cert.candidate_count} candidate{cert.candidate_count !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="w-16 bg-green-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, (cert.candidate_count / analytics.total_candidates) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
              <div className="text-center pt-2 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {analytics.candidates_with_certifications} of {analytics.total_candidates} candidates have certifications
                </p>
                <p className="text-xs text-gray-500">
                  Avg: {analytics.avg_certifications_per_candidate} certifications per candidate
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <p className="text-gray-500">No certifications data</p>
            </div>
          )}
        </div>

        {/* Top Languages */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Languages</h3>
          {analytics?.top_languages && analytics.top_languages.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_languages.map((lang, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">{lang.language_name}</p>
                    <p className="text-xs text-blue-600">{lang.candidate_count} candidate{lang.candidate_count !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="w-16 bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, (lang.candidate_count / analytics.total_candidates) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
              <div className="text-center pt-2 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {analytics.candidates_with_languages} of {analytics.total_candidates} candidates speak multiple languages
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
              </div>
              <p className="text-gray-500">No languages data</p>
            </div>
          )}
        </div>
      </div>

      {/* Project Technologies & Awards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Top Project Technologies */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Project Technologies</h3>
          {analytics?.top_project_technologies && analytics.top_project_technologies.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_project_technologies.map((tech, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-indigo-900">{tech.technology_name}</p>
                    <p className="text-xs text-indigo-600">{tech.project_count} project{tech.project_count !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="w-16 bg-indigo-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, (tech.project_count / 10) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
              <div className="text-center pt-2 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {analytics.candidates_with_projects} of {analytics.total_candidates} candidates have projects
                </p>
                <p className="text-xs text-gray-500">
                  Avg: {analytics.avg_projects_per_candidate} projects per candidate
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 bg-indigo-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <p className="text-gray-500">No project technologies data</p>
            </div>
          )}
        </div>

        {/* Awards Distribution */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Awards & Achievements</h3>
          {analytics?.awards_distribution && analytics.awards_distribution.length > 0 ? (
            <div className="space-y-3">
              {analytics.awards_distribution.map((award, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-900">{award.award_name}</p>
                    <p className="text-xs text-yellow-600">{award.candidate_count} candidate{award.candidate_count !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="w-16 bg-yellow-200 rounded-full h-2">
                    <div 
                      className="bg-yellow-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, (award.candidate_count / analytics.total_candidates) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
              <div className="text-center pt-2 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {analytics.candidates_with_awards} of {analytics.total_candidates} candidates have awards
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <p className="text-gray-500">No awards data</p>
            </div>
          )}
        </div>
      </div>

      {/* Search Analytics */}
      {analytics?.search_analytics && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Search Analytics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {analytics.search_analytics.total_searches}
              </p>
              <p className="text-sm text-gray-500">Total Searches</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {analytics.search_analytics.avg_results_per_search.toFixed(1)}
              </p>
              <p className="text-sm text-gray-500">Avg Results per Search</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {analytics.search_analytics.top_search_terms?.length || 0}
              </p>
              <p className="text-sm text-gray-500">Unique Search Terms</p>
            </div>
          </div>

          {analytics.search_analytics.top_search_terms && analytics.search_analytics.top_search_terms.length > 0 && (
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Top Search Terms</h4>
              <div className="flex flex-wrap gap-2">
                {analytics.search_analytics.top_search_terms.slice(0, 10).map((term, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    {term.term}
                    <span className="ml-2 text-blue-600">({term.search_count})</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {canUploadResumes && (
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
            <h3 className="text-lg font-semibold mb-2">Upload Resumes</h3>
            <p className="text-indigo-100 mb-4">Add new candidates to your database with AI-powered parsing.</p>
            {isSubscriptionAllowed ? (
              <Link
                to="/company/upload"
                className="inline-block bg-white text-indigo-600 px-4 py-2 rounded-md font-medium hover:bg-gray-100 transition-colors"
              >
                Upload Now
              </Link>
            ) : (
              <span className="inline-block bg-white/50 text-indigo-300 px-4 py-2 rounded-md font-medium cursor-not-allowed" title="Subscription required">
                Upload Now
              </span>
            )}
          </div>
        )}

        <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-lg p-6 text-white">
          <h3 className="text-lg font-semibold mb-2">Search Candidates</h3>
          <p className="text-green-100 mb-4">Find the perfect candidates using advanced filters and AI matching.</p>
          {isSubscriptionAllowed ? (
            <Link
              to="/company/candidates"
              className="inline-block bg-white text-green-600 px-4 py-2 rounded-md font-medium hover:bg-gray-100 transition-colors"
            >
              Search Now
            </Link>
          ) : (
            <span className="inline-block bg-white/50 text-green-300 px-4 py-2 rounded-md font-medium cursor-not-allowed" title="Subscription required">
              Search Now
            </span>
          )}
        </div>

        {canManageJobs && (
          <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg p-6 text-white">
            <h3 className="text-lg font-semibold mb-2">Job Management</h3>
            <p className="text-blue-100 mb-4">Create and manage job postings, track applications and matches.</p>
            <Link 
              to="/company/jobs"
              className="inline-block bg-white text-blue-600 px-4 py-2 rounded-md font-medium hover:bg-gray-100 transition-colors"
            >
              Manage Jobs
            </Link>
          </div>
        )}

        {canManageUsers && (
          <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-lg p-6 text-white">
            <h3 className="text-lg font-semibold mb-2">Manage Users</h3>
            <p className="text-orange-100 mb-4">Add team members and manage access to your recruitment platform.</p>
            <Link 
              to="/company/users"
              className="inline-block bg-white text-orange-600 px-4 py-2 rounded-md font-medium hover:bg-gray-100 transition-colors"
            >
              Manage Users
            </Link>
          </div>
        )}

        <div className="bg-gradient-to-r from-gray-600 to-gray-800 rounded-lg p-6 text-white">
          <h3 className="text-lg font-semibold mb-2">Contact Support</h3>
          <p className="text-gray-100 mb-4">Need help? Get in touch with our support team for assistance.</p>
          <button 
            onClick={() => setShowSupportForm(true)}
            className="inline-block bg-white text-gray-600 px-4 py-2 rounded-md font-medium hover:bg-gray-100 transition-colors"
          >
            Get Support
          </button>
        </div>
      </div>

      {/* Support Ticket Form Modal */}
      <SupportTicketForm
        isOpen={showSupportForm}
        onClose={() => setShowSupportForm(false)}
        source="COMPANY_PORTAL"
        userInfo={{
          name: `${user?.first_name} ${user?.last_name}`,
          email: user?.email,
          phone: user?.phone
        }}
      />
    </div>
  );
};

export default CompanyDashboard; 